import React, {
  useMemo,
  useRef,
  useState,
  useEffect,
  useCallback,
} from 'react';
import classNames from 'classnames';
import { Overlay } from 'ncoded-component-library';
import Portal from 'components/Portal';
import usePositioning from './hooks/usePositioning';
import { TRANSITION_TIME } from 'data/global';
import { XOR } from 'types';
import useMatchMedia from 'hooks/useMatchMedia';
import useAddEventListener from 'hooks/useAddEventListener';

import './ResolvePopup.styles.scss';

type OpenOnHover = {
  onHover: true;
  customOnHover?: () => void;
};

type OpenOnState = {
  open: boolean;
};

export type ResolvePopupProps = {
  className?: string;
  portalClassName?: string;
  popupContent: React.ReactNode;
  contentClassName?: string;
  setRef?: React.MutableRefObject<HTMLElement>;
  style?: React.CSSProperties;
  preferUpwards?: boolean;
  closeOnScroll?: boolean;
  centerOnVisibleTrigger?: boolean;
  onCloseOnScroll?: () => void;
} & XOR<OpenOnHover, OpenOnState>;

const ResolvePopup: React.FC<ResolvePopupProps> = (props) => {
  const {
    children,
    className,
    popupContent,
    portalClassName,
    contentClassName,
    setRef,
    style,
    open,
    onHover,
    preferUpwards,
    closeOnScroll,
    centerOnVisibleTrigger,
    customOnHover,
    onCloseOnScroll,
  } = props;

  const [isUpwards, setIsUpwards] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  const popupRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const isTouchDevice = useMatchMedia('isTouchDevice');

  const closeOnPointerLeave = useCallback(() => {
    if (!onHover) return;

    setIsOpen(false);
  }, [onHover]);

  const { isPositioned } = usePositioning({
    popupRef,
    triggerRef,
    contentRef,
    isMounted,
    preferUpwards,
    centerOnVisibleTrigger,
    setIsUpwards,
    onPointerLeave: closeOnPointerLeave,
  });

  const classes = classNames(
    'resolve-popup',
    { 'resolve-popup--open': isOpen, 'resolve-popup--upwards': isUpwards },
    className,
  );
  const portalClasses = classNames('resolve-popup__portal', portalClassName);
  const contentClasses = classNames(
    'resolve-popup__content',
    {
      'resolve-popup__content--open': isOpen && isPositioned,
      'resolve-popup__content--upwards': isUpwards,
    },
    contentClassName,
  );
  const triggerClasses = classNames(
    'resolve-popup__trigger',
    {
      'resolve-popup__trigger--open': isOpen,
      'resolve-popup__trigger--upwards': isUpwards,
    },
    contentClassName,
  );

  const content = useMemo(
    () => (
      <>
        {isMounted && (
          <Overlay
            open
            elevate
            parentEl={document.body}
            noContent
            background="transparent"
            className="resolve-popup__overlay"
          />
        )}
        <div className={contentClasses} ref={contentRef}>
          <div className="resolve-popup__content__content-wrapper">
            <div className="resolve-popup__content__content-wrapper__container">
              {popupContent}
            </div>
          </div>
        </div>
      </>
    ),
    [isMounted, contentClasses, popupContent],
  );

  const openOnMouse = useCallback(() => {
    if (!onHover || isTouchDevice) return;

    setIsOpen((prev) => {
      if (prev) return prev;

      customOnHover?.();
      return true;
    });
  }, [customOnHover, onHover, isTouchDevice]);

  // touch has no hover: a tap must open (and toggle) the popup directly,
  // otherwise the browser eats the first tap as a synthetic hover
  const toggleOnTap = useCallback(() => {
    if (!onHover || !isTouchDevice) return;

    setIsOpen((prev) => {
      if (prev) return false;

      customOnHover?.();
      return true;
    });
  }, [customOnHover, onHover, isTouchDevice]);

  const closeOnOutsideTap = useCallback((event: PointerEvent) => {
    const target = event.target as Node;

    if (
      popupRef.current?.contains(target) ||
      contentRef.current?.contains(target)
    )
      return;

    setIsOpen(false);
  }, []);

  useAddEventListener({
    event: 'pointerdown',
    handler: closeOnOutsideTap,
    capture: true,
    enabled: !!onHover && isTouchDevice && isOpen,
  });

  useEffect(() => {
    if (!onHover) setIsOpen(open);
  }, [onHover, open]);

  // outlive `isOpen` so the content can fade out before it leaves the portal
  useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
      return;
    }

    const timeout = setTimeout(() => setIsMounted(false), TRANSITION_TIME);

    return () => clearTimeout(timeout);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !closeOnScroll) return;

    const close = (event: Event) => {
      const target = event.target as Node;

      if (target && contentRef.current?.contains(target)) return;

      setIsOpen(false);
      onCloseOnScroll?.();
    };

    window.addEventListener('scroll', close, true);
    return () => window.removeEventListener('scroll', close, true);
  }, [closeOnScroll, isOpen, onCloseOnScroll]);

  return (
    <div
      className={classes}
      style={style}
      onMouseOver={openOnMouse}
      onMouseLeave={() => onHover && !isTouchDevice && setIsOpen(false)}
      ref={(r) => {
        popupRef.current = r;
        if (setRef) setRef.current = r;
      }}
    >
      <Portal className={portalClasses} isOpen={isMounted}>
        {content}
      </Portal>
      <div className={triggerClasses} ref={triggerRef} onClick={toggleOnTap}>
        {children}
      </div>
    </div>
  );
};

export default ResolvePopup;
